// ═══════════════════════════════════════════════════════════════
// COMPONENTE: Comparación de Nóminas
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import {
  GitCompare,
  TrendingUp,
  TrendingDown,
  Minus,
  Users,
  DollarSign,
  Calendar,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { usePayroll } from '../../hooks/usePayroll';
import { useCompany } from '../../contexts/CompanyContext';

const fmt = (v) =>
  parseFloat(v || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtPct = (v) => {
  const n = parseFloat(v || 0);
  return n >= 0 ? `+${Math.abs(n).toFixed(2)}%` : `-${Math.abs(n).toFixed(2)}%`;
};

const variationIcon = (pct) => {
  if (pct > 0) return <TrendingUp  className="w-4 h-4 text-green-600" />;
  if (pct < 0) return <TrendingDown className="w-4 h-4 text-red-600"   />;
  return             <Minus        className="w-4 h-4 text-gray-400"  />;
};

const variationCls = (pct) =>
  pct > 0 ? 'text-green-700' : pct < 0 ? 'text-red-700' : 'text-gray-600';

export default function PayrollComparison() {
  const { getPayrolls, comparePayrolls, loading } = usePayroll();
  const { currentCompany } = useCompany();

  const [payrolls,        setPayrolls]        = useState([]);
  const [selectedPayroll1, setSelectedPayroll1] = useState('');
  const [selectedPayroll2, setSelectedPayroll2] = useState('');
  const [comparisonData,  setComparisonData]  = useState(null);

  useEffect(() => {
    if (currentCompany?.id) loadPayrolls();
  }, [currentCompany]); // eslint-disable-line

  const loadPayrolls = async () => {
    try {
      const data = await getPayrolls(currentCompany.id);
      setPayrolls(Array.isArray(data) ? data.filter(p => p.estado === 'closed') : []);
    } catch (error) {
      console.error('Error loading payrolls:', error);
    }
  };

  const handleCompare = async () => {
    if (!selectedPayroll1 || !selectedPayroll2) { alert('Debe seleccionar dos nóminas para comparar'); return; }
    if (selectedPayroll1 === selectedPayroll2)  { alert('Debe seleccionar dos nóminas diferentes');    return; }
    try {
      const result = await comparePayrolls(selectedPayroll1, selectedPayroll2);
      setComparisonData(result.comparison);
    } catch (error) {
      alert('Error al comparar nóminas: ' + error.message);
    }
  };

  const canCompare = selectedPayroll1 && selectedPayroll2 && selectedPayroll1 !== selectedPayroll2;

  return (
    <div className="space-y-4">

      {/* ── Toolbar ──────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
          <span className="font-semibold text-gray-700">{payrolls.length}</span> nóminas cerradas
        </span>
      </div>

      {/* ── Selector ─────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <p className="text-xs text-gray-400 mb-4">
          El orden no importa — las nóminas se compararán automáticamente de la más antigua a la más reciente.
        </p>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">Primera Nómina</label>
            <select
              value={selectedPayroll1}
              onChange={(e) => setSelectedPayroll1(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
            >
              <option value="">Seleccione una nómina...</option>
              {payrolls.map(p => (
                <option key={p.id} value={p.id}>
                  {p.periodo} — {p.quincena || 'Mensual'} ({p.tipo})
                </option>
              ))}
            </select>
          </div>

          <div className="flex-shrink-0 pt-4">
            <GitCompare size={16} className="text-gray-300" />
          </div>

          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">Segunda Nómina</label>
            <select
              value={selectedPayroll2}
              onChange={(e) => setSelectedPayroll2(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
            >
              <option value="">Seleccione una nómina...</option>
              {payrolls.map(p => (
                <option key={p.id} value={p.id}>
                  {p.periodo} — {p.quincena || 'Mensual'} ({p.tipo})
                </option>
              ))}
            </select>
          </div>

          <div className="flex-shrink-0 pt-4">
            <button
              onClick={handleCompare}
              disabled={loading || !canCompare}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <GitCompare size={14} />
              {loading ? 'Comparando...' : 'Comparar'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Resultados ───────────────────────────────────────────── */}
      {comparisonData && (
        <div className="space-y-4">

          {/* Info de las dos nóminas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-5 border-l-4 border-l-blue-500">
              <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Calendar size={12} /> Nómina 1 — Anterior
              </p>
              <div className="space-y-1.5 text-sm text-gray-700">
                <p><span className="font-medium text-gray-500">Período:</span> {comparisonData.nominas.nomina1.periodo}</p>
                <p><span className="font-medium text-gray-500">Quincena:</span> {comparisonData.nominas.nomina1.quincena || 'Mensual'}</p>
                <p><span className="font-medium text-gray-500">Tipo:</span> {comparisonData.nominas.nomina1.tipo.toUpperCase()}</p>
                <p><span className="font-medium text-gray-500">Fecha Pago:</span> {new Date(comparisonData.nominas.nomina1.fecha_pago.split('T')[0] + 'T12:00:00').toLocaleDateString('es-DO')}</p>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-5 border-l-4 border-l-gray-400">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Calendar size={12} /> Nómina 2 — Más Reciente
              </p>
              <div className="space-y-1.5 text-sm text-gray-700">
                <p><span className="font-medium text-gray-500">Período:</span> {comparisonData.nominas.nomina2.periodo}</p>
                <p><span className="font-medium text-gray-500">Quincena:</span> {comparisonData.nominas.nomina2.quincena || 'Mensual'}</p>
                <p><span className="font-medium text-gray-500">Tipo:</span> {comparisonData.nominas.nomina2.tipo.toUpperCase()}</p>
                <p><span className="font-medium text-gray-500">Fecha Pago:</span> {new Date(comparisonData.nominas.nomina2.fecha_pago.split('T')[0] + 'T12:00:00').toLocaleDateString('es-DO')}</p>
              </div>
            </div>
          </div>

          {/* Resumen de variaciones */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { label: 'Variación Neto a Pagar',    value: fmtPct(comparisonData.resumen.variacion_neto),          pct: comparisonData.resumen.variacion_neto },
              { label: 'Variación Costo Empresa',   value: fmtPct(comparisonData.resumen.variacion_costo_empresa), pct: comparisonData.resumen.variacion_costo_empresa },
              { label: 'Cambio en Empleados',
                value: (comparisonData.resumen.total_empleados_cambio >= 0 ? '+' : '') + comparisonData.resumen.total_empleados_cambio,
                pct: comparisonData.resumen.total_empleados_cambio },
            ].map(({ label, value, pct }) => (
              <div key={label} className="bg-white border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-500 mb-1">{label}</p>
                <p className={`text-2xl font-bold ${variationCls(pct)}`}>{value}</p>
              </div>
            ))}
          </div>

          {/* Totales */}
          <ComparisonTable
            title="Totales Generales"
            icon={<DollarSign size={14} className="text-gray-400" />}
            data={[
              { label: 'Neto a Pagar',        ...comparisonData.diferencias.neto_a_pagar },
              { label: 'Costo Total Empresa',  ...comparisonData.diferencias.costo_total_empresa },
            ]}
          />

          {/* Ingresos */}
          <ComparisonTable
            title="Ingresos"
            icon={<TrendingUp size={14} className="text-gray-400" />}
            data={Object.entries(comparisonData.diferencias.ingresos).map(([key, val]) => ({
              label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
              ...val
            }))}
          />

          {/* Deducciones */}
          <ComparisonTable
            title="Deducciones"
            icon={<TrendingDown size={14} className="text-gray-400" />}
            data={Object.entries(comparisonData.diferencias.deducciones).map(([key, val]) => ({
              label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
              ...val
            }))}
          />

          {/* Análisis de empleados */}
          {comparisonData.analisis_empleados && (
            <EmployeeAnalysis data={comparisonData.analisis_empleados} />
          )}
        </div>
      )}
    </div>
  );
}

// ── Tabla de comparación ──────────────────────────────────────────
function ComparisonTable({ title, icon, data }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
        {icon}
        <span className="text-sm font-semibold text-gray-700">{title}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-blue-600 text-white">
              <th className="px-5 py-3 text-left   text-xs font-semibold uppercase tracking-wider">Concepto</th>
              <th className="px-5 py-3 text-right  text-xs font-semibold uppercase tracking-wider">Nómina 1</th>
              <th className="px-5 py-3 text-right  text-xs font-semibold uppercase tracking-wider">Nómina 2</th>
              <th className="px-5 py-3 text-right  text-xs font-semibold uppercase tracking-wider">Variación</th>
              <th className="px-5 py-3 text-right  text-xs font-semibold uppercase tracking-wider">Diferencia</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {data.map((item, i) => (
              <tr key={i} className="hover:bg-gray-50 transition-colors">
                <td className="px-5 py-3 text-sm font-medium text-gray-900">{item.label}</td>
                <td className="px-5 py-3 text-sm text-gray-500 text-right">RD$ {fmt(item.nomina1)}</td>
                <td className="px-5 py-3 text-sm font-semibold text-gray-900 text-right">RD$ {fmt(item.nomina2)}</td>
                <td className="px-5 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {variationIcon(item.porcentaje)}
                    <span className={`text-sm font-bold ${variationCls(item.porcentaje)}`}>
                      {fmtPct(item.porcentaje)}
                    </span>
                  </div>
                </td>
                <td className="px-5 py-3 text-sm text-gray-500 text-right">
                  RD$ {fmt(Math.abs(item.diferencia))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Análisis de empleados ─────────────────────────────────────────
function EmployeeAnalysis({ data }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors border-b border-gray-100"
      >
        <div className="flex items-center gap-2">
          <Users size={14} className="text-gray-400" />
          <span className="text-sm font-semibold text-gray-700">Análisis de Empleados</span>
        </div>
        {expanded
          ? <ChevronUp  size={14} className="text-gray-400" />
          : <ChevronDown size={14} className="text-gray-400" />}
      </button>

      {expanded && (
        <div className="p-5 space-y-4">
          {/* Mini stat cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-500 mb-1">Empleados Nuevos</p>
              <p className="text-2xl font-bold text-blue-600">{data.nuevos.length}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-500 mb-1">Empleados Removidos</p>
              <p className="text-2xl font-bold text-red-600">{data.removidos.length}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-xs text-gray-500 mb-1">Con Cambios Significativos</p>
              <p className="text-2xl font-bold text-gray-900">{data.con_cambios.length}</p>
            </div>
          </div>

          {/* Empleados con cambios */}
          {data.con_cambios.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Cambios significativos (&gt;5%)
              </p>
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="w-full">
                  <thead>
                    <tr className="bg-blue-600 text-white">
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Empleado</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Neto Anterior</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Neto Actual</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Variación</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {data.con_cambios.slice(0, 10).map((emp, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <p className="text-sm font-semibold text-gray-900">{emp.nombre}</p>
                          <p className="text-xs text-gray-400">{emp.codigo}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 text-right">RD$ {fmt(emp.neto_anterior)}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">RD$ {fmt(emp.neto_actual)}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {variationIcon(emp.porcentaje)}
                            <span className={`text-sm font-bold ${variationCls(emp.porcentaje)}`}>
                              {fmtPct(emp.porcentaje)}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
